<script setup>
import { addDynamicRoutes } from '@/router';
import { useMenuStore } from '@/store/menu-store';
import { useRouterStore } from '@/store/router-store';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'; // watch 추가
import { useRouter } from 'vue-router';

const menuStore = useMenuStore();
const router = useRouter();
const routerStore = useRouterStore();
const cachedTabs = ref([]);
const tabScrollContainer = ref(null);
const scrollAmount = 200;
const scrollLeftDisabled = ref(true);
const scrollRightDisabled = ref(false);
const showScrollButtons = ref(false);

const activateTab = async (menuItemCode) => {
    menuStore.setActiveTab(menuItemCode);
    const tab = menuStore.getTabByCode(menuItemCode);
    if (tab) {
        if (!cachedTabs.value.includes(menuItemCode)) {
            cachedTabs.value.push(menuItemCode);
        }
        await navigateTo(tab.menu_item_code);
    }
};

const navigateTo = async (menuItemCode) => {
    const tab = menuStore.getTabByCode(menuItemCode);
    const targetName = tab ? tab.menu_item_code : 'dashboard';

    await router.push({
        name: targetName,
        replace: true,
        state: history.state ? { ...history.state } : null
    });
};

const closeTab = async (menuItemCode) => {
    menuStore.removeTab(menuItemCode);
    cachedTabs.value = cachedTabs.value.filter((tab) => tab !== menuItemCode);

    const openTabs = menuStore.openTabs;
    if (openTabs.length === 0) {
        await navigateTo('dashboard');
        await addDynamicRoutes(router, routerStore.routerList);
        return;
    }

    if (menuItemCode === menuStore.activeTab) {
        const lastTab = openTabs[openTabs.length - 1];
        menuStore.setActiveTab(lastTab.menu_item_code);
        await navigateTo(lastTab.menu_item_code);
    } else {
        await navigateTo(menuStore.activeTab);
    }
    updateScrollButtonState(); // 탭 닫은 후 상태 업데이트
};

const closeAllTabsExceptActive = () => {
    const activeTabCode = menuStore.activeTab;
    menuStore.openTabs = menuStore.openTabs.filter((tab) => tab.menu_item_code === activeTabCode);
    updateScrollButtonState();
    nextTick(() => {
        updateScrollButtonState();
    });
};

const closeAllTabsOnRefresh = () => {
    menuStore.clearTabs();
};

const scrollTabs = (direction) => {
    if (tabScrollContainer.value) {
        const container = tabScrollContainer.value;
        const currentScroll = container.scrollLeft;

        let newScroll;
        if (direction === 'left') {
            newScroll = currentScroll - scrollAmount;
        } else {
            newScroll = currentScroll + scrollAmount;
        }

        container.scrollTo({
            left: newScroll,
            behavior: 'smooth'
        });
    }
};

const updateScrollButtonState = () => {
    const container = tabScrollContainer.value;

    if (container) {
        scrollLeftDisabled.value = container.scrollLeft <= 0;
        scrollRightDisabled.value = container.scrollLeft >= container.scrollWidth - container.clientWidth;
    }

    // 화면 너비가 768px 미만이면 화살표 버튼 숨김, 그렇지 않으면 탭 개수에 따라 표시
    if (window.innerWidth < 768) {
        showScrollButtons.value = false;
    } else if (menuStore.openTabs.length > 1 && window.innerWidth < 1050) {
        showScrollButtons.value = true;
    } else {
        showScrollButtons.value = menuStore.openTabs.length > 5;
    }
};

const handleScroll = () => {
    updateScrollButtonState();
};

onMounted(() => {
    window.addEventListener('beforeunload', closeAllTabsOnRefresh);

    if (tabScrollContainer.value) {
        tabScrollContainer.value.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('resize', updateScrollButtonState);
    updateScrollButtonState();
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', closeAllTabsOnRefresh);
    if (tabScrollContainer.value) {
        tabScrollContainer.value.removeEventListener('scroll', handleScroll);
    }
    window.removeEventListener('resize', updateScrollButtonState);
});

const showCloseAllButton = computed(() => {
    // 탭이 2개 이상 *이거나* 스크롤 버튼이 표시될 때 전체 닫기 버튼 표시 (OR 조건)
    return menuStore.openTabs.length > 1 || showScrollButtons.value;
});
watch(
    () => menuStore.openTabs.length,
    () => {
        updateScrollButtonState();
    },
    { immediate: true } // 컴포넌트가 마운트될 때 즉시 실행
);
</script>

<template>
    <div class="app-menu-tab">
        <button v-if="showScrollButtons" class="scroll-button scroll-left" @click="scrollTabs('left')" :disabled="scrollLeftDisabled">
            <i class="pi pi-chevron-left"></i>
        </button>
        <div class="tab-scroll-container" ref="tabScrollContainer">
            <div v-for="tab in menuStore.openTabs" :key="tab.menu_item_code" :class="['tab', { active: tab.menu_item_code === menuStore.activeTab }]" @click="activateTab(tab.menu_item_code)">
                {{ tab.menu_item_name }}
                <button class="close-button" @click.stop="closeTab(tab.menu_item_code)">
                    <i class="pi pi-times"></i>
                </button>
            </div>
            <button v-if="showCloseAllButton" class="close-all-tabs-button tab" @click="closeAllTabsExceptActive">전체 닫기</button>
        </div>
        <button v-if="showScrollButtons" class="scroll-button scroll-right" @click="scrollTabs('right')" :disabled="scrollRightDisabled">
            <i class="pi pi-chevron-right"></i>
        </button>
    </div>
</template>

<style scoped>
.app-menu-tab {
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    position: relative;
    padding: 0 1rem 0 1rem;
    box-sizing: border-box;
}

.tab-scroll-container {
    overflow-x: auto;
    white-space: nowrap;
    display: flex;
    flex-grow: 1;
    min-width: 0;
    -ms-overflow-style: none;
    scrollbar-width: none;
    scroll-behavior: smooth;
}

.tab-scroll-container::-webkit-scrollbar {
    display: none;
}

.tab {
    display: flex;
    align-items: center;
    padding: 0 1rem;
    height: 100%;
    font-family: 'SCoreDream400';
    font-size: 0.85rem;
    color: #5f6368;
    cursor: pointer;
    transition: background-color 0.2s;
    border-right: 1px solid #e0e0e0;
    flex-shrink: 0;
}

.tab:hover {
    background-color: #f1f3f4;
    border-radius: 1rem;
}

.tab.active {
    background-color: #e8f0fe;
    color: #0038ea;
}

.tab.active:hover {
    border-radius: 1rem;
}

.close-button {
    margin-left: 0.4rem;
    background: none;
    border: none;
    /* opacity: 0.7;
    border-radius: 50%; */
}

.pi-times {
    font-size: 0.65rem;
    padding: 0;
    opacity: 0.6;
}

.pi-times:hover {
    opacity: 1;
}

.close-button:hover {
    opacity: 1;
}

.close-all-tabs-button {
    background-color: transparent;
    border: none;
    white-space: nowrap;
    flex-shrink: 0;
    border-right: none;
}

.scroll-button {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.5rem;
    color: #555;
    transition: color 0.2s;
    z-index: 1001;
    flex-shrink: 0;
    position: absolute;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
}

.scroll-button.scroll-left {
    left: 0;
}

.scroll-button.scroll-right {
    right: 0;
}

.scroll-button:hover {
    color: #1a73e8;
}

.scroll-button:disabled {
    color: #ccc;
    cursor: default;
}
</style>
